import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

/**
 * Saying that something worked.
 *
 * The counter needs this more than most software does. A staff member presses
 * "Take the booking" with a customer on the phone and looks away at the car
 * park; when they look back they need to know, in one glance, whether the thing
 * is written down. Silence reads as failure, and the failure they assume is
 * always "press it again" — which is how you get two bookings.
 *
 * So: a short sentence, bottom of the screen, gone in a few seconds. It is
 * `aria-live="polite"` and outside the routed content, which means it survives
 * the navigation that usually follows a save — the toast is still there when
 * the next screen has already drawn.
 */

type Tone = 'done' | 'problem';

interface Toast {
  readonly id: number;
  readonly message: string;
  readonly tone: Tone;
}

interface ToastApi {
  /** "Booking BK-26-0041 saved." Past tense, and name the thing. */
  readonly done: (message: string) => void;
  readonly problem: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Long enough to read a sentence twice, short enough not to sit in the way. */
const LINGER_MS = 4500;

export function ToastProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef<number[]>([]);

  const push = useCallback((message: string, tone: Tone) => {
    const id = nextId.current++;
    setToasts((current) => [...current, { id, message, tone }]);
    const timer = window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, LINGER_MS);
    timers.current.push(timer);
  }, []);

  // Cleared on unmount, so a toast timer cannot fire into a torn-down tree.
  useEffect(() => () => timers.current.forEach(window.clearTimeout), []);

  const api = useMemo<ToastApi>(
    () => ({
      done: (message: string) => push(message, 'done'),
      problem: (message: string) => push(message, 'problem'),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/* Always rendered, so the live region exists before the first message
          does — a region created at the same moment as its content is a region
          screen readers routinely miss. */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-3 sm:p-4"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={[
              'pointer-events-auto flex w-full max-w-md items-start gap-3 rounded border px-4 py-3 shadow-sm',
              toast.tone === 'done'
                ? 'border-signal bg-ink text-ink-inverse'
                : 'border-alert bg-alert-wash text-alert',
            ].join(' ')}
          >
            <span className="min-w-0 flex-1 text-body font-medium">{toast.message}</span>
            <button
              type="button"
              onClick={() => setToasts((current) => current.filter((t) => t.id !== toast.id))}
              className={[
                'shrink-0 rounded px-1 text-small underline-offset-2 hover:underline',
                toast.tone === 'done' ? 'text-ink-inverse/80' : 'text-alert',
              ].join(' ')}
            >
              Dismiss
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) throw new Error('useToast used outside ToastProvider');
  return api;
}
